import { sortByKey } from 'state/auctions.slice/helpers';
import {
  didFilterAuctions,
  didFilterMyAuctions,
  generateFeFilters,
  generateFilters,
} from './helpers';

export function filterMatchByStoneType(filters: string[], auction: AuctionsTypes.AuctionType) {
  const showNatural = filters.includes('Natural') && auction.diamond_type === 'Natural';
  const showLabGrown = filters.includes('Lab Grown') && auction.diamond_type === 'Lab Grown';
  const showGemstones = filters.includes('Gemstone') && 'gemstone_type' in auction;

  return showNatural || showLabGrown || showGemstones;
}

export function filterMatchByTreatedStone(filters: string[], auction: AuctionsTypes.AuctionType) {
  const showNonTreated = filters.includes('No') && !auction.treated;
  const showTreated = filters.includes('Yes') && auction.treated;
  return showNonTreated || showTreated;
}

function filterMatchByCertificates(search: string, certificates: AuctionsTypes.CertificatesType) {
  if (!certificates || !certificates.provider_and_number) {
    return false;
  }

  return Object.values(certificates.provider_and_number).some((certificate) => {
    const { provider, number } = certificate;

    const normalizedProvider = provider ? provider.toLowerCase().trim() : '';
    const normalizedNumber = number ? number.toLowerCase().trim() : '';
    const normalizedCertificate = normalizedProvider + normalizedNumber;

    return normalizedCertificate.includes(search);
  });
}

export function filterMatchBySearch(search: string, auction: AuctionsTypes.AuctionType) {
  const formattedSearch = search.toLocaleLowerCase().trim().replace(/\s/gi, '');
  if (String(auction.id).includes(formattedSearch)) {
    return true;
  }
  if (filterMatchByCertificates(formattedSearch, auction.certificates)) {
    return true;
  }
  return false;
}

export function filterMatchByWatch(
  watchList: { [key: number | string]: boolean },
  auction: AuctionsTypes.AuctionType,
) {
  return watchList[auction.auction_id];
}

export function filterMatchByPosition(
  inTheLead: boolean | undefined,
  outbid: boolean | undefined,
  auction: AuctionsTypes.AuctionType,
) {
  if (!auction.your_bid || !auction.lead_bid) {
    return false;
  }
  if (inTheLead && outbid) {
    return true;
  }
  if (inTheLead) {
    return auction.your_bid >= auction.lead_bid;
  }
  if (outbid) {
    return auction.your_bid < auction.lead_bid;
  }
  return false;
}

export function filterMatchBySuggested(suggested: number[], auction: AuctionsTypes.AuctionType) {
  return suggested.includes(auction.auction_id);
}

export function filterMatchByNotViewed(viewed: number[], auction: AuctionsTypes.AuctionType) {
  return !viewed.includes(auction.auction_id);
}

export function filterMatchByTrending(trending: number[], auction: AuctionsTypes.AuctionType) {
  return trending.includes(auction.auction_id);
}

export function sortAuctions(
  sort: AuctionsTypes.SortType,
  auctions: AuctionsTypes.AuctionType[],
): AuctionsTypes.AuctionType[] {
  return sortByKey(auctions, sort.col, !sort.asc);
}

function filter(data: {
  feFilters: AuctionsTypes.FeFiltersType;
  auctions: AuctionsTypes.AuctionType[];
  watchList?: { [key: number | string]: boolean };
  search?: string;
  suggested?: number[];
  viewed?: number[];
  trending?: number[];
}): AuctionsTypes.AuctionType[] {
  const { feFilters, auctions, watchList, search, suggested, viewed, trending } = data;
  if (Object.keys(feFilters).length === 0 && !search) {
    return auctions;
  }

  return auctions.filter((auction) => {
    if (feFilters.stoneType && !filterMatchByStoneType(feFilters.stoneType, auction)) {
      return false;
    }
    if (feFilters.treatedStone && !filterMatchByTreatedStone(feFilters.treatedStone, auction)) {
      return false;
    }
    if (search && !filterMatchBySearch(search, auction)) {
      return false;
    }
    if (feFilters.watch && watchList && !filterMatchByWatch(watchList, auction)) {
      return false;
    }
    if (feFilters.suggested && suggested && !filterMatchBySuggested(suggested, auction)) {
      return false;
    }
    if (feFilters.trending && trending && !filterMatchByTrending(trending, auction)) {
      return false;
    }
    if (feFilters.notViewed && viewed && !filterMatchByNotViewed(viewed, auction)) {
      return false;
    }
    if (
      (feFilters.inTheLead || feFilters.outbid) &&
      !filterMatchByPosition(feFilters.inTheLead, feFilters.outbid, auction)
    ) {
      return false;
    }
    return true;
  });
}

export function filterAuctions(
  type: AuctionsTypes.AuctionTypes,
  state: RootState,
  auctions: AuctionsTypes.AuctionType[],
) {
  const watchList = state.auctionsWatch.auctions;
  const { filters, quickFilters, personalizedFilters, filtersData, suggested, viewed, trending } =
    state.auctions;
  const search = state.auctions.search[type];
  const feFilters = generateFeFilters([
    ...filters[type],
    ...quickFilters[type],
    ...personalizedFilters[type],
  ]);
  const beFilters = generateFilters(type, filters[type], filtersData);
  const isFiltered = didFilterAuctions(feFilters, beFilters, search);

  const filteredAuctions = filter({
    feFilters,
    auctions,
    watchList,
    search,
    suggested,
    viewed,
    trending,
  });

  return {
    isFiltered,
    auctions: filteredAuctions,
    count: filteredAuctions.length,
  };
}

export function filterMyAuctions(
  type: MyAuctionsTypes.MyAuctionsTypes,
  state: RootState,
  auctions: AuctionsTypes.AuctionType[],
) {
  const { quickFilters } = state.myAuctions;
  const feFilters = generateFeFilters([...quickFilters[type]]);
  const beFilters = {};
  const search = '';

  const isFiltered = didFilterMyAuctions(beFilters, feFilters, search);

  const filteredAuctions = filter({ feFilters, auctions });

  return {
    isFiltered,
    auctions: filteredAuctions as MyAuctionsTypes.MyAuctionType[],
    count: filteredAuctions.length,
  };
}
